import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import type { JSX } from "react";
import { ProtectedRoute } from "components";
import { Footer, Header, Loading } from "@dsaui/components";
import AuthLayout from "components/AuthLayout";
import routes from "routes";
import AccountPage from "pages/AccountPage";
import MembershipPage from "pages/MembershipPage";
import LoginPage from "pages/LoginPage";
import ErrorPage from "pages/ErrorPage";
import SignupPage from "pages/SignupPage";
import DashboardPage from "pages/DashboardPage";
import PreferencesPage from "pages/PreferencesPage";
import ProfilePage from "pages/ProfilePage";
import AuthenticationErrorPage from "pages/AuthenticationErrorPage";
import { useAppContext } from "utils/hooks";

interface AppRouterProps {
  notification: JSX.Element | null;
}

const AppRouter = ({ notification }: AppRouterProps): JSX.Element => {
  const { ready, authenticated, authenticationError, keycloak } = useAppContext();

  if (!ready) {
    return <Loading fullscreen />;
  }

  if (authenticationError) {
    const retryAuthentication = (): void => {
      keycloak.clearToken();
      void Promise.resolve(keycloak.login({ prompt: "login" })).catch(console.error);
    };

    return (
      <AuthLayout>
        <AuthenticationErrorPage onRetry={retryAuthentication} />
      </AuthLayout>
    );
  }

  const routesComponent = (
    <Routes>
      <Route
        path={routes.internal.signup}
        element={
          <AuthLayout>
            <SignupPage />
          </AuthLayout>
        }
      />
      <Route
        path={routes.internal.login}
        element={
          <AuthLayout>
            <LoginPage loginFn={keycloak.login} />
          </AuthLayout>
        }
      />
      <Route element={<ProtectedRoute />}>
        <Route path={routes.internal.base} element={<DashboardPage />} />
        <Route
          path={routes.internal.profile}
          element={<ProfilePage variant="profile" />}
        />
        <Route path={routes.internal.account} element={<AccountPage />} />
        <Route path={routes.internal.membership} element={<MembershipPage />} />
        <Route path={routes.internal.preferences} element={<PreferencesPage />} />
        <Route path={routes.unlisted.error} element={<ErrorPage />} />
      </Route>
      <Route path="*" element={<ErrorPage />} />
    </Routes>
  );

  return (
    <Router>
      {authenticated ? (
        <div className="flex min-h-screen flex-col">
          <Header
            loginFn={keycloak.login}
            logoutFn={keycloak.logout}
            authenticated={authenticated}
          />
          <div className="flex flex-1 flex-col bg-gray-50">{routesComponent}</div>
          {notification}
          <Footer />
        </div>
      ) : (
        <>
          {routesComponent}
          {notification}
        </>
      )}
    </Router>
  );
};

export default AppRouter;
